import type { JSX } from 'react';
import { Topbar } from './AdminLayout.js';
import { Icon } from '../components/Icon.js';
import { Sparkline, StatusChip } from '../components/ui.js';
import { MOCK } from '../mock.js';
import { fmtAmount, fmtAmountPlain, relativeTime } from '../utils/format.js';

function StatCard({
  label,
  value,
  sub,
  trend,
}: {
  label: string;
  value: string | number;
  sub: string;
  trend?: number;
}): JSX.Element {
  return (
    <div
      className="card"
      style={{
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{label}</div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: -0.5,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--fg-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {trend !== undefined && (
          <span
            style={{
              color:
                trend > 0 ? 'var(--accent-subtle-fg)' : 'var(--danger-subtle-fg)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Icon name={trend > 0 ? 'arrowUp' : 'arrowDown'} size={11} />
            {Math.abs(trend)}%
          </span>
        )}
        {sub}
      </div>
    </div>
  );
}

export function Dashboard(): JSX.Element {
  const pays = MOCK.payments;
  const succ = pays.filter((p) => p.status === 'succeeded');
  const revIls = succ
    .filter((p) => p.currency === 'ILS')
    .reduce((s, p) => s + p.amount, 0);
  const revRub = succ
    .filter((p) => p.currency === 'RUB')
    .reduce((s, p) => s + p.amount, 0);
  const todaySeries = [12, 19, 14, 22, 28, 26, 31, 29, 38, 42, 35, 44];
  const recent = pays.slice(0, 6);

  return (
    <>
      <Topbar title="Дашборд" mock />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 14,
            marginBottom: 18,
          }}
        >
          <StatCard
            label="Всего платежей"
            value={pays.length.toLocaleString('ru-RU')}
            sub="за всё время"
            trend={12}
          />
          <StatCard
            label="Успешные"
            value={succ.length}
            sub={`из ${pays.length} · ${Math.round((succ.length / pays.length) * 100)}%`}
            trend={4}
          />
          <StatCard
            label="Выручка · ILS"
            value={fmtAmountPlain(revIls)}
            sub="без налога"
            trend={8}
          />
          <StatCard
            label="Выручка · RUB"
            value={'₽ ' + fmtAmountPlain(revRub)}
            sub="без комиссии"
            trend={-2}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Платежи за 12 часов</div>
                <div className="card-subtitle">все продукты, все статусы</div>
              </div>
              <div className="seg">
                <button type="button">24ч</button>
                <button type="button" className="active">
                  12ч
                </button>
                <button type="button">1ч</button>
              </div>
            </div>
            <div style={{ padding: '24px 20px 18px' }}>
              <svg viewBox="0 0 600 160" style={{ width: '100%', height: 160 }}>
                <defs>
                  <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <line
                    key={i}
                    x1="0"
                    x2="600"
                    y1={40 * i + 10}
                    y2={40 * i + 10}
                    stroke="var(--hairline)"
                    strokeWidth="1"
                  />
                ))}
                {(() => {
                  const data = todaySeries;
                  const pts = data.map((v, i) => [
                    (i / (data.length - 1)) * 580 + 10,
                    150 - (v / Math.max(...data)) * 130,
                  ]);
                  const d = pts
                    .map((p, i) => (i ? 'L' : 'M') + p[0] + ' ' + p[1])
                    .join(' ');
                  return (
                    <g>
                      <path d={d + ' L 590 150 L 10 150 Z'} fill="url(#ga)" />
                      <path
                        d={d}
                        fill="none"
                        stroke="var(--accent)"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      {pts.map((p, i) => (
                        <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="var(--accent)" />
                      ))}
                    </g>
                  );
                })()}
              </svg>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: 'var(--fg-subtle)',
                  fontFamily: 'var(--font-mono)',
                  marginTop: 6,
                }}
              >
                {['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Последние события</div>
            </div>
            <div>
              {recent.map((p, i) => (
                <div
                  key={p.id}
                  style={{
                    padding: '10px 20px',
                    borderBottom: i === recent.length - 1 ? 'none' : '1px solid var(--hairline)',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <StatusChip status={p.status} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <bdi>{p.customerName}</bdi>
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: 'var(--fg-subtle)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <bdi>{p.productName}</bdi>
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontSize: 12,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{fmtAmount(p.amount, p.currency)}</div>
                    <div style={{ color: 'var(--fg-subtle)', fontSize: 11 }}>
                      {relativeTime(p.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-header">
            <div className="card-title">Топ-продукты</div>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Продукт</th>
                <th>Slug</th>
                <th style={{ textAlign: 'right' }}>Платежей (mock)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {MOCK.products.slice(0, 5).map((p) => {
                const total = pays.filter((x) => x.productId === p.id).length;
                return (
                  <tr key={p.id} className="no-hover">
                    <td style={{ fontWeight: 500 }}><bdi>{p.name}</bdi></td>
                    <td>
                      <span className="mono subtle" style={{ fontSize: 12 }}>
                        {p.slug}
                      </span>
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {total}
                    </td>
                    <td style={{ width: 140, paddingRight: 20 }}>
                      <Sparkline data={[2, 4, 3, 6, 5, 8, 6, 9, 7, 10, 8, 11]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
